import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ROUTES, type CardView } from '@serb/shared';
import { api } from '../../api/client';
import { KEYS } from '../../api/keys';
import { SAMPLE_DATA } from '../../lib/sample';
import { cardToApproval } from './cardMap';
import type { Talk } from './CardChat';
import { APPROVALS, type Approval, type Decision } from './sample';

/** An email as you can change it on a card */
export type EmailFields = { to: string; cc: string; subject: string; body: string; attachments: string[] };

const SAMPLE_KEY = ['approvals-sample'];
const now = () =>
  new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Amman' });

/**
 * The approval cards and what you decided on them. They are made by Claude on the server and kept there; with sample data on,
 * made-up cards are kept in the browser instead. Approving never sends anything.
 */
export function useApprovals() {
  const qc = useQueryClient();
  const real = useQuery({
    queryKey: KEYS.cards,
    queryFn: () => api.get<CardView[]>(ROUTES.api.cards),
    enabled: !SAMPLE_DATA,
  });
  const sample = useQuery({
    queryKey: SAMPLE_KEY,
    queryFn: async () => APPROVALS,
    enabled: SAMPLE_DATA,
    staleTime: Infinity,
    gcTime: Infinity,
  });
  const items: Approval[] = SAMPLE_DATA
    ? (sample.data ?? [])
    : (real.data ?? []).flatMap((c) => cardToApproval(c) ?? []);
  const refresh = () => qc.invalidateQueries({ queryKey: KEYS.cards });

  const decide = async (id: string, status: Decision, note: string) => {
    if (SAMPLE_DATA) {
      qc.setQueryData<Approval[]>(SAMPLE_KEY, (all = []) =>
        all.map((a) => (a.id === id ? { ...a, status, history: [...a.history, [now(), note]] } : a)),
      );
      return;
    }
    await api.post(ROUTES.api.cardDecide(id), {
      action: status === 'approved' ? 'approve' : 'reject',
      note,
      device: 'app',
    });
    await refresh();
  };

  /** Approve your own version of an email: you changed the text, subject, people or files. Claude's draft is kept for learning. */
  const edit = async (id: string, final: EmailFields, note: string) => {
    if (SAMPLE_DATA) return decide(id, 'approved', note);
    await api.post(ROUTES.api.cardDecide(id), { action: 'edit', final, note, device: 'app' });
    await refresh();
  };

  /** Tell Claude what to change on a card, a rule to follow from now on, or ask it a question. The server wakes it for this within a minute. */
  const instruct = async (id: string, text: string, kind: Talk) => {
    if (SAMPLE_DATA) return;
    await api.post(ROUTES.api.cardInstruct(id), {
      text,
      rule: kind === 'rule',
      question: kind === 'question',
      device: 'app',
    });
    await refresh();
  };

  return {
    items,
    loading: SAMPLE_DATA ? sample.isPending : real.isPending,
    error: real.isError,
    decide,
    edit,
    instruct,
  };
}

/** How many cards wait for your decision */
export const useApprovalCount = () => useApprovals().items.filter((a) => a.status === 'pending').length;

/** How many cards wait on one shipment: counted from the cards themselves, so every page agrees */
export function useApprovalsFor() {
  const { items } = useApprovals();
  return (srb: string) => items.filter((a) => a.srb === srb && a.status === 'pending').length;
}
